import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function AdminChats() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();

  // Chat state
  const [chatContacts, setChatContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [chatGroup, setChatGroup] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [contactType, setContactType] = useState('all'); // 'all', 'students', 'teachers', 'parents'
  const chatMessagesEndRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchChatContacts();
    }
  }, [schoolData?.id, selectedBranchId, contactType]);

  useEffect(() => {
    if (selectedContact && schoolData) {
      loadOrCreateChatGroup();
    }

    // Cleanup on unmount
    return () => {
      if (window.currentAdminChatChannel) {
        supabase.removeChannel(window.currentAdminChatChannel);
        window.currentAdminChatChannel = null;
      }
    };
  }, [selectedContact, schoolData]);

  useEffect(() => {
    if (chatMessages.length > 0) {
      setTimeout(() => {
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [chatMessages.length]);

  const fetchChatContacts = async () => {
    if (!schoolData?.id) return;

    try {
      setLoadingContacts(true);
      const contacts = [];

      // Fetch students
      if (contactType === 'all' || contactType === 'students') {
        let studentsQuery = supabase
          .from('students')
          .select('id, first_name, last_name, email, class_id, section_id')
          .eq('school_id', schoolData.id)
          .eq('is_active', true);

        if (selectedBranchId) {
          studentsQuery = studentsQuery.eq('branch_id', selectedBranchId);
        }

        const { data: students } = await studentsQuery;
        if (students) {
          students.forEach(student => {
            contacts.push({
              id: student.id,
              name: `${student.first_name} ${student.last_name}`,
              email: student.email,
              type: 'student',
              class_id: student.class_id,
              section_id: student.section_id
            });
          });
        }
      }

      // Fetch teachers
      if (contactType === 'all' || contactType === 'teachers') {
        let teachersQuery = supabase
          .from('teachers')
          .select('id, first_name, last_name, email')
          .eq('school_id', schoolData.id)
          .eq('is_active', true);

        if (selectedBranchId) {
          teachersQuery = teachersQuery.eq('branch_id', selectedBranchId);
        }

        const { data: teachers } = await teachersQuery;
        if (teachers) {
          teachers.forEach(teacher => {
            contacts.push({
              id: teacher.id,
              name: `${teacher.first_name} ${teacher.last_name}`,
              email: teacher.email,
              type: 'teacher'
            });
          });
        }
      }

      // Fetch parents
      if (contactType === 'all' || contactType === 'parents') {
        let parentsQuery = supabase
          .from('parents')
          .select('id, first_name, last_name, email')
          .eq('school_id', schoolData.id)
          .eq('is_active', true);

        if (selectedBranchId) {
          parentsQuery = parentsQuery.eq('branch_id', selectedBranchId);
        }

        const { data: parents } = await parentsQuery;
        if (parents) {
          parents.forEach(parent => {
            contacts.push({
              id: parent.id,
              name: `${parent.first_name} ${parent.last_name}`,
              email: parent.email,
              type: 'parent'
            });
          });
        }
      }

      // Sort by name
      contacts.sort((a, b) => a.name.localeCompare(b.name));
      setChatContacts(contacts);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadOrCreateChatGroup = async () => {
    if (!selectedContact || !schoolData) return;

    try {
      setChatLoading(true);
      const groupName = `Admin Chat: ${selectedContact.name}`;

      // Check if chat group already exists
      let { data: existingGroups, error: groupError } = await supabase
        .from('chat_groups')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('group_type', 'custom')
        .ilike('name', `%${selectedContact.name}%`);

      let group = null;

      if (existingGroups && existingGroups.length > 0) {
        // Check if both admin and contact are members
        for (const g of existingGroups) {
          const { data: members } = await supabase
            .from('chat_group_members')
            .select('*')
            .eq('group_id', g.id);

          const hasContact = members?.some(m => 
            m.user_type === selectedContact.type && 
            m.user_id === selectedContact.id.toString()
          );
          const hasAdmin = members?.some(m => 
            m.user_type === 'school' && 
            m.user_id === schoolData.id.toString()
          );

          if (hasContact && hasAdmin) {
            group = g;
            break;
          }
        }
      }

      // Create new group if doesn't exist
      if (!group) {
        const { data: newGroup, error: createError } = await supabase
          .from('chat_groups')
          .insert({
            school_id: schoolData.id,
            name: groupName,
            group_type: 'custom',
            created_by: 'school'
          })
          .select()
          .single();

        if (createError) {
          console.error('Error creating chat group:', createError);
          alert('Failed to create chat. Please try again.');
          return;
        }

        group = newGroup;

        // Add contact as member
        await supabase
          .from('chat_group_members')
          .insert({
            group_id: group.id,
            user_type: selectedContact.type,
            user_id: selectedContact.id.toString(),
            display_name: selectedContact.name
          });

        // Add admin as member
        await supabase
          .from('chat_group_members')
          .insert({
            group_id: group.id,
            user_type: 'school',
            user_id: schoolData.id.toString(),
            display_name: 'School Admin'
          });
      }

      setChatGroup(group);

      // Load messages
      const { data: messages, error: messagesError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('group_id', group.id)
        .order('created_at', { ascending: true });

      if (messagesError) {
        console.error('Error loading messages:', messagesError);
      } else {
        setChatMessages(messages || []);
      }

      // Clean up existing subscription
      if (window.currentAdminChatChannel) {
        supabase.removeChannel(window.currentAdminChatChannel);
        window.currentAdminChatChannel = null;
      }

      // Set up real-time subscription
      const channel = supabase
        .channel(`admin-chat:${group.id}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `group_id=eq.${group.id}`
        }, (payload) => {
          const newMessage = payload.new;
          
          setChatMessages(prev => {
            const exists = prev.some(m => m.id === newMessage.id);
            if (exists) return prev;
            return [...prev, newMessage].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          });

          setTimeout(() => {
            if (chatMessagesEndRef.current) {
              chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        })
        .subscribe();

      window.currentAdminChatChannel = channel;
    } catch (err) {
      console.error('Error loading chat:', err);
      alert('Failed to load chat. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };

  // Upload files to Supabase storage
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return [];
    
    const uploadedUrls = [];
    setUploadingFiles(true);
    
    try {
      const bucketName = 'chat-uploads';
      
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `chat_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${schoolData.id}/${fileName}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          if (uploadError.message.includes('Bucket not found')) {
            alert('Storage bucket not found. Please create "chat-uploads" bucket in Supabase Storage.');
            continue;
          }
          continue;
        }
        
        const { data: urlData } = supabase.storage
          .from(bucketName)
          .getPublicUrl(filePath);
        
        uploadedUrls.push({
          url: urlData.publicUrl,
          name: file.name,
          type: file.type,
          size: file.size
        });
      }
    } catch (err) {
      console.error('Error uploading files:', err);
    } finally {
      setUploadingFiles(false);
    }
    
    return uploadedUrls;
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || !chatGroup || !schoolData) return;

    try {
      let mediaUrls = [];
      if (selectedFiles.length > 0) {
        mediaUrls = await uploadFiles(selectedFiles);
        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      const { data: message, error } = await supabase
        .from('chat_messages')
        .insert({
          school_id: schoolData.id,
          group_id: chatGroup.id,
          sender_type: 'school',
          sender_id: schoolData.id.toString(),
          content: newMessage.trim() || null,
          media_urls: mediaUrls.length > 0 ? mediaUrls : null
        })
        .select()
        .single();

      if (error) {
        console.error('Error sending message:', error);
        alert('Failed to send message. Please try again.');
        return;
      }

      // Add message immediately
      setChatMessages(prev => {
        const exists = prev.some(m => m.id === message.id);
        if (exists) return prev;
        return [...prev, message].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      });
      
      setNewMessage('');

      // Scroll to bottom
      setTimeout(() => {
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum file size is 10MB.`);
        return false;
      }
      return true;
    }).slice(0, 5);
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      navigate('/schoollogin');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      navigate('/schoollogin');
    }
  };

  const filteredContacts = chatContacts.filter(contact => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return contact.name.toLowerCase().includes(query) ||
           (contact.email && contact.email.toLowerCase().includes(query));
  });

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
      />
      
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      <div className="main-content">
        <div className="content-header">
          <h1>💬 Admin Chats</h1>
          <p>Chat with any student, teacher, or parent</p>
        </div>

        <div className="content-body">
          <div className="chat-main-container" style={{ display: 'flex', height: 'calc(100vh - 200px)' }}>
            {/* Left Sidebar - Contacts List */}
            <div className="chat-sidebar" style={{ width: '350px', borderRight: '1px solid #E5E7EB', padding: '20px' }}>
              <div className="chat-filter-tabs" style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
                <button
                  className={`btn-small ${contactType === 'all' ? 'active' : ''}`}
                  onClick={() => setContactType('all')}
                  style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #E5E7EB', background: contactType === 'all' ? '#FF8B3D' : 'white', color: contactType === 'all' ? 'white' : '#1F2937' }}
                >
                  All
                </button>
                <button
                  className={`btn-small ${contactType === 'students' ? 'active' : ''}`}
                  onClick={() => setContactType('students')}
                  style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #E5E7EB', background: contactType === 'students' ? '#FF8B3D' : 'white', color: contactType === 'students' ? 'white' : '#1F2937' }}
                >
                  Students
                </button>
                <button
                  className={`btn-small ${contactType === 'teachers' ? 'active' : ''}`}
                  onClick={() => setContactType('teachers')}
                  style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #E5E7EB', background: contactType === 'teachers' ? '#FF8B3D' : 'white', color: contactType === 'teachers' ? 'white' : '#1F2937' }}
                >
                  Teachers
                </button>
                <button
                  className={`btn-small ${contactType === 'parents' ? 'active' : ''}`}
                  onClick={() => setContactType('parents')}
                  style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #E5E7EB', background: contactType === 'parents' ? '#FF8B3D' : 'white', color: contactType === 'parents' ? 'white' : '#1F2937' }}
                >
                  Parents
                </button>
              </div>

              <input
                type="text"
                className="chat-search-input"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', padding: '10px', marginBottom: '16px', borderRadius: '8px', border: '1px solid #E5E7EB' }}
              />

              {loadingContacts ? (
                <div className="loading-container">
                  <p>Loading contacts...</p>
                </div>
              ) : filteredContacts.length === 0 ? (
                <div className="empty-state">
                  <p>No contacts found.</p>
                </div>
              ) : (
                <div className="chat-contacts-list">
                  {filteredContacts.map(contact => (
                    <div
                      key={`${contact.type}-${contact.id}`}
                      className={`chat-contact-card ${selectedContact?.id === contact.id && selectedContact?.type === contact.type ? 'selected' : ''}`}
                      onClick={() => setSelectedContact(contact)}
                      style={{ 
                        padding: '12px', 
                        marginBottom: '8px', 
                        borderRadius: '8px', 
                        cursor: 'pointer',
                        background: selectedContact?.id === contact.id && selectedContact?.type === contact.type ? '#FFF4ED' : 'white',
                        border: selectedContact?.id === contact.id && selectedContact?.type === contact.type ? '2px solid #FF8B3D' : '1px solid #E5E7EB'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '50%', 
                          background: '#FF8B3D', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '18px',
                          fontWeight: 'bold'
                        }}>
                          {contact.type === 'student' ? '👨‍🎓' : contact.type === 'teacher' ? '👨‍🏫' : '👨‍👩‍👧‍👦'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: '#1F2937' }}>{contact.name}</div>
                          <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                            {contact.type.charAt(0).toUpperCase() + contact.type.slice(1)}
                            {contact.email && ` • ${contact.email}`}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Chat Area */}
            {selectedContact ? (
              <div className="chat-window-modern" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div className="chat-window-header-modern" style={{ padding: '16px', borderBottom: '1px solid #E5E7EB' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '50%', 
                      background: '#FF8B3D', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '18px'
                    }}>
                      {selectedContact.type === 'student' ? '👨‍🎓' : selectedContact.type === 'teacher' ? '👨‍🏫' : '👨‍👩‍👧‍👦'}
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>{selectedContact.name}</h3>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#6B7280' }}>
                        {selectedContact.type.charAt(0).toUpperCase() + selectedContact.type.slice(1)}
                      </p>
                    </div>
                  </div>
                </div>

                {chatLoading ? (
                  <div className="chat-loading-state">
                    <div className="loading-spinner">⏳</div>
                    <p>Loading chat...</p>
                  </div>
                ) : (
                  <>
                    <div className="chat-messages-area" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                      {chatMessages.length === 0 ? (
                        <div className="chat-no-messages">
                          <p>No messages yet. Start the conversation!</p>
                        </div>
                      ) : (
                        <>
                          {chatMessages.map(message => {
                            const isOwnMessage = message.sender_type === 'school';
                            
                            return (
                              <div
                                key={message.id}
                                className={`chat-message ${isOwnMessage ? 'sent' : 'received'}`}
                                style={{ marginBottom: '16px' }}
                              >
                                <div className="chat-message-content">
                                  <div className="chat-message-sender">
                                    {isOwnMessage ? 'You (Admin)' : selectedContact.name}
                                  </div>
                                  {message.content && <div className="chat-message-text">{message.content}</div>}
                                  {message.media_urls && Array.isArray(message.media_urls) && message.media_urls.length > 0 && (
                                    <div className="chat-message-media" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      {message.media_urls.map((media, idx) => (
                                        <div key={idx}>
                                          {media.type && media.type.startsWith('image/') ? (
                                            <img
                                              src={media.url}
                                              alt={media.name || 'Image'}
                                              style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', cursor: 'pointer' }}
                                              onClick={() => window.open(media.url, '_blank')}
                                            />
                                          ) : (
                                            <a
                                              href={media.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 12px',
                                                background: '#F3F4F6',
                                                borderRadius: '8px',
                                                textDecoration: 'none',
                                                color: '#1F2937',
                                                maxWidth: '300px'
                                              }}
                                            >
                                              <span>📄</span>
                                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {media.name || 'Document'}
                                              </span>
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="chat-message-time">
                                    {new Date(message.created_at).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          <div ref={chatMessagesEndRef} />
                        </>
                      )}
                    </div>

                    <div className="chat-input-area-modern" style={{ padding: '16px', borderTop: '1px solid #E5E7EB' }}>
                      {selectedFiles.length > 0 && (
                        <div className="chat-selected-files" style={{ padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                          {selectedFiles.map((file, index) => (
                            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: '#E5E7EB', borderRadius: '4px', fontSize: '12px' }}>
                              <span>{file.name}</span>
                              <button
                                type="button"
                                onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/*,.pdf,.doc,.docx,.txt"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                          id="chat-file-input-admin"
                        />
                        <label
                          htmlFor="chat-file-input-admin"
                          style={{ cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F4F6', borderRadius: '8px', minWidth: '40px', flexShrink: 0 }}
                          title="Attach file"
                        >
                          📎
                        </label>
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                          placeholder="Type your message..."
                          className="chat-input-modern"
                          style={{ flex: 1, minWidth: 0, width: '100%' }}
                        />
                        <button
                          type="button"
                          onClick={sendMessage}
                          disabled={(!newMessage.trim() && selectedFiles.length === 0) || uploadingFiles}
                          className="chat-send-button"
                          title="Send message"
                        >
                          {uploadingFiles ? '⏳' : '➤'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="chat-empty-state" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
                  <h3 style={{ margin: 0, marginBottom: '8px' }}>Select a Contact</h3>
                  <p style={{ margin: 0, color: '#6B7280' }}>Choose a contact from the list to start chatting</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminChats;
